import React from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import useAuth from "../../system/APIAuth";
import { IconButton, ListItemIcon, Menu, MenuItem, Box, Divider } from '@mui/material';
import { AutoAwesome, Logout, Settings, SupervisedUserCircle, Groups, Search } from '@mui/icons-material';
import { InfegyUser, currentUser, permissionTest } from 'system/InfegyUser';
import Avatar from "components/uiElements/avatar/Avatar";
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import LabelIcon from '@mui/icons-material/Label';

export default function AvatarMenu() {
  const auth = useAuth();
  const user = currentUser();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const isInQuickSearch = window.location.pathname.includes("/quick-search");

  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const hasQueryMonitorsAccess = user?.permissions.includes("query_alerts")
    && user.permissions.includes("social_data");

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  const handleUserMenuProfileClick = () => {
    setUserMenuAnchorEl(null);
    navigate('/me');
  };
  function handleClickUsage() {
    setUserMenuAnchorEl(null);
    navigate("/usage");
  }
  function handleClickAdmin() {
    setUserMenuAnchorEl(null);
    navigate("/admin");
  }
  function handleClickQueryManagement() {
    setUserMenuAnchorEl(null);
    navigate("/query-management");
  }
  function handleClickTagManagement() {
    setUserMenuAnchorEl(null);
    navigate("/tag-management");
  }
  function handleClickTeams() {
    setUserMenuAnchorEl(null);
    navigate("/teams");
  }
  function handleClickPulseAI() {
    setUserMenuAnchorEl(null);
    navigate("/ai-assistant");
  }
  function handleQueryMonitors() {
    setUserMenuAnchorEl(null);
    navigate("/query-monitors")
  }
  function handleQuickSearch() {
    setUserMenuAnchorEl(null);
    navigate("/quick-search")
  }
  const handleUserMenuLogoutClick = async () => {
    setUserMenuAnchorEl(null);
    await auth.logOut();
    window.location.href = "/login";
  };

  return (
    <Box sx={{ display: "inline-block", verticalAlign: "middle" }}>
      {user &&
        <>
          <IconButton onClick={handleAvatarClick}>
            <Avatar user={user as InfegyUser} />
          </IconButton>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={() => setUserMenuAnchorEl(null)} >
            {/* Group 1: Account */}
            <MenuItem onClick={handleUserMenuProfileClick}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              {i18n.t("nav.account")}
            </MenuItem>
            {auth.user?.role !== 'ai-studio' && permissionTest("api") &&
              <MenuItem onClick={handleClickUsage}>
                <ListItemIcon>
                  <DataUsageIcon fontSize="small" />
                </ListItemIcon>
                {i18n.t("nav.usage")}
              </MenuItem>
            }
            {permissionTest("account_admin") &&
              <MenuItem onClick={handleClickAdmin}>
                <ListItemIcon>
                  <SupervisedUserCircle fontSize="small" />
                </ListItemIcon>
                {i18n.t("nav.admin")}
              </MenuItem>
            }

            <Divider sx={{ margin: "10px 0" }} />

            {/* Group 2: Resources */}
            <MenuItem onClick={handleClickQueryManagement}>
              <ListItemIcon>
                <ManageSearchIcon fontSize="small" />
              </ListItemIcon>
              {i18n.t("nav.query-management")}
            </MenuItem>
            <MenuItem onClick={handleClickTagManagement}>
              <ListItemIcon>
                <LabelIcon fontSize="small" />
              </ListItemIcon>
              {i18n.t("nav.tag-management")}
            </MenuItem>
            {auth.user?.role !== 'ai-studio' && (
              <MenuItem onClick={handleClickTeams}>
                <ListItemIcon>
                  <Groups fontSize="small" />
                </ListItemIcon>
                {i18n.t("nav.teams")}
              </MenuItem>
            )}

            <Divider sx={{ margin: "10px 0" }} />

            {/* Group 3: Features */}
            <MenuItem
              disabled={!permissionTest("ai_features")}
              onClick={handleClickPulseAI}>
              <ListItemIcon>
                <AutoAwesome fontSize="small" />
              </ListItemIcon>
                {i18n.t("nav.pulse-ai")}
            </MenuItem>
            {permissionTest("run_query") && (
              <>
              {!!isInQuickSearch && (
                <MenuItem onClick={() => {
                  sessionStorage.setItem("skipQuickSearchRedirect", "true");
                  navigate("/start")
                }}>
                  <ListItemIcon>
                    <Search fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("advanced-search")}
                </MenuItem>
              )}
              {!isInQuickSearch && (
                <MenuItem onClick={handleQuickSearch}>
                  <ListItemIcon>
                    <Search fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("quick-search")}
                </MenuItem>
              )}
              </>
            )}
            {hasQueryMonitorsAccess &&
              <MenuItem onClick={handleQueryMonitors}>
                <ListItemIcon>
                  <MonitorHeartIcon fontSize="small" />
                </ListItemIcon>
                {i18n.t("nav.query-monitors")}
              </MenuItem>
            }

            <Divider sx={{ margin: "10px 0" }} />

            <MenuItem onClick={handleUserMenuLogoutClick}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              {i18n.t("nav.logout")}
            </MenuItem>
          </Menu>
        </>
      }
    </Box>
  );
}